define('history',[
  'homeStatusList',
  'paramsObserver',
  'queryStringConfig',
  'searchTypeHelper',
  'uriSegmentConfig',
  'urlHelper'],
(
  homeStatusList,
  paramsObserver,
  queryStringConfig,
  searchTypeHelper,
  uriSegmentConfig,
  urlHelper
) => {
  'use strict';

  let queryStringParameters = [];
  let temporalFiltersModified = [];
  const entrySrpFunction = searchTypeHelper.getSrpFunction();
  const searchResultFunctions = searchTypeHelper.getAllSrpFunctions();
  const entryValue = Object.entries(searchResultFunctions).find((srpFunction) => srpFunction[1] === entrySrpFunction);
  const uriSegments = searchTypeHelper.getAllSrpTypes().getAllFunctions();
  const srpFunctionKey = document.documentElement.lang === 'en' ? 'nhs' : 'cna';
  // For an existing issue, https://builderhomesite.atlassian.net/browse/BDXNHS-8129,
  // we will keep the functionality only with the word 'page' in the url to avoid some issues with the pagination.
  // When the ticket 8129 is closed, we have to update this pageWord variable to translate the page 'word' when CNA.
  const pageWord = srpFunctionKey === 'nhs' ? 'page' : 'pagina';
  let uriSegmentParameter = entryValue[0].replace(searchTypeHelper.getSrpType(), '').replace('__', '');

  function clearTemporalFilters() {
    temporalFiltersModified = [];
  }

  function getQueryStringsApplied(data) {
    let filter;
    let index;
    const length = temporalFiltersModified.length;

    for (index = 0; index < length; index += 1) {
      filter = temporalFiltersModified[index];
      queryStringConfig.getQueryString(queryStringParameters, filter, data.parameters[filter]);
    }

    const keysParameters = Object.keys(data.parametersChanged);

    for (let propIndex = 0; propIndex < keysParameters.length; propIndex += 1) {
      if (Object.prototype.hasOwnProperty.call(data.parametersChanged, keysParameters[propIndex])) {
        queryStringConfig.getQueryString(queryStringParameters, keysParameters[propIndex],
          data.parametersChanged[keysParameters[propIndex]]);
      }
    }

    temporalFiltersModified = [];
  }

  function insertQueryString(url, queryString) {
    let firstQuestion = url.indexOf('?') + 1;
    let queryStringToAppend = queryString;
    const isStatePage = url.indexOf('/state/') > -1;

    if (!queryString) {
      queryStringToAppend = '';

      if (firstQuestion > 0) {
        firstQuestion -= 1;
      } else if (firstQuestion === 0) {
        firstQuestion = url.length;
      }
    }

    if (queryString && firstQuestion === 0) {
      queryStringToAppend = '?' + queryString;
      firstQuestion = url.length;
    }

    if (isStatePage) {
      queryStringToAppend = '';
    }

    return url.substring(0, firstQuestion) + queryStringToAppend;
  }

  // This function moves the Query strings from the actual link to a new link
  function getNewLinkWithQueryStrings(url) {
    let text = '';
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    const paramsKeys = Object.keys(params);
    for (let index = 0; index < paramsKeys.length; index += 1) {
      if (index > 0) {
        text = `${text}&`;
      }
      text = `${text}${paramsKeys[index]}=${urlSearchParams.get(paramsKeys[index])}`;
    }

    return insertQueryString(url, text);
  }

  /*
  * Gets the URL function according to the filters
  * @param {Object} The "tree" of the filters with their SRP type.
  * @param {Object} The object with the path to the filter applied.
  * @param {Object} The list of parameters.
  * returns The new URL with the Function url updated.
  */
  function getUriSegmentRecursive(tree, path, parameters) {
    const key = path.shift();
    const valueToSearch = typeof parameters[key] === 'string' ? parameters[key] : key;
    const leaf = tree[valueToSearch];

    if (!leaf) {
      return '';
    }

    let uriSegment = leaf[srpFunctionKey];

    if (path.length) {
      const temporalValue = getUriSegmentRecursive(leaf, path, parameters);

      if (temporalValue) {
        uriSegment = temporalValue;
      }
    }

    return uriSegment;
  }

  function insertUrlSegment(url, urlSegment) {
    const firstQuestion = url.indexOf('?');
    const lastSlash = url.lastIndexOf('/') + 1;
    const length = url.length + 1;
    let position;

    if (lastSlash === length) {
      position = lastSlash;
    } else if (length > firstQuestion && firstQuestion > 0) {
      position = firstQuestion;
    } else {
      position = length;
    }

    return url.slice(0, position) + urlSegment + url.slice(position);
  }

  function resetFilters() {
    queryStringParameters = [];
    temporalFiltersModified = [];
  }

  /**
   * Validates if beta cookie it´s set as true.
   * @param {url} url The new page number of the search.
   */
  function validateRedirectToBeta(url) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${"OptInBetaSite_1"}=`);
    const needRedirect = parts.pop().split(';').shift();
    if (needRedirect.toLowerCase() === 'true') {
      location.href = url;
    }
  }


  /**
   * Gets the updated url with the new page.
   * @param {Number} newPage The new page number of the search.
   * @returns The url updated with the new information.
   */
  function updatePaginationUrl(newPage) {
    const url = window.location.href.trim().replace(/\/$/, '');
    const previousPage = urlHelper.getValueFromQueryString(pageWord, window.location.href) ||
    urlHelper.getValueFromUrlSegment(pageWord, window.location.href);
    let newPageAsQueryString = `${pageWord}=${newPage}`;
    let newPageAsUrlSegment = `/${pageWord}-${newPage}`;

    if (newPage === 1) {
      newPageAsQueryString = '';
      newPageAsUrlSegment = '';
    }

    if (previousPage) {
      return url.replace(`${pageWord}=${previousPage}`, newPageAsQueryString)
        .replace(`/${pageWord}-${previousPage}`, newPageAsUrlSegment);
    }

    return insertUrlSegment(url, newPageAsUrlSegment);
  }

  function updateQueryStringUrl(data, currentUrl) {
    const url = currentUrl || window.location.href.trim('/').replace(/\/$/, '');
    getQueryStringsApplied(data);

    const queryString = queryStringParameters.length > 0 ? queryStringParameters[0] : null;
    return insertQueryString(url, queryString);
  }

  /*
  * Excludes the brand parameter from the Url.
  * @param {String} The String with the current url.
  * @param {String} The current brand id value.
  * returns The new URL without the brand parameter.
  */
  function getUrlWithoutBrandParam(url) {
    const urlObject = new URL(url);
    const urlCurrentItems = urlObject.pathname.split('/');
    const isBrandPartnerNhs = document.documentElement.lang === 'en';
    const builderParam = isBrandPartnerNhs ? 'builder-' : 'constructor-';

    const urlItems = urlCurrentItems.filter(item => !item.includes(builderParam));

    urlObject.pathname = urlItems.join('/');

    return urlObject.href;
  }

  /*
  * Gets the URL with the brand id modified if it was modified.
  * @param {String} The String with the new function of the url.
  * @param {String} The String with the current url.
  * @param {Object} The list of parameters.
  * returns The new URL with the Function url updated.
  */
  function updateBrandParameter(newSearchFunction, currentUrl, data) {
    const currentSrpType = paramsObserver.getParameter('SrpType');
    const currentBrandId = paramsObserver.getParameter('BrandId');
    const isBrandPartnerNhs = document.documentElement.lang === 'en';
    const builderParam = isBrandPartnerNhs ? 'builder-' : 'constructor-';

    if (newSearchFunction === searchResultFunctions.BuilderResults ||
        (searchTypeHelper.isCustomBuilderSearchType(currentSrpType))) {

        var url = getUrlWithoutBrandParam(currentUrl);
        return insertQueryString(url);
    }

    // When the filter is cleared
    if (currentBrandId === 0 && currentUrl.includes(builderParam)) {
        return getUrlWithoutBrandParam(currentUrl);
    }

    if (currentBrandId > 0 &&
        (searchTypeHelper.isCommunitySearchType(currentSrpType)
            || searchTypeHelper.isHomeSearchType(currentSrpType))) {

        return currentUrl;
    }

    return currentUrl;
  }

  function updateTemporalFilters(data) {
    temporalFiltersModified = [];

    const keysParameters = Object.keys(data.parametersChanged);

    for (let index = 0; index < keysParameters.length; index += 1) {
      if (Object.prototype.hasOwnProperty.call(data.parametersChanged, keysParameters[index])) {
        temporalFiltersModified.push(keysParameters[index]);
      }
    }
  }

  /**
   * Updates the URL with the actual postal code if it has.
   * @param {String} currentUrl The current URL of the page.
   * @param {String} currentPostalCode The current postal code of the page.
   * @param {Boolean} isBuilderSearch The bollean value if the builder tab is selected or not.
   * @returns The updated URL if there is a currentPostalCode.
   */
  function updatePostalCodeBuilderUrl(currentUrl, currentPostalCode, isBuilderSearch) {
    const postalCodePrimaryCity = paramsObserver.getParameter('City').toLowerCase().replace(' ', '-');
    let url = currentUrl;
    if (url !== '') {
      if (currentPostalCode !== '') {
        if (isBuilderSearch) {
          url = currentUrl.replace(currentPostalCode, postalCodePrimaryCity);
        } else {
          const newURL = new URL(currentUrl);
          const pathNameValues = newURL.pathname.split('/');
          let postalCodeIndex = pathNameValues.findIndex((element) => element.includes(pageWord)) - 1;
          postalCodeIndex = postalCodeIndex > 0 ? postalCodeIndex : pathNameValues.length - 1;
          pathNameValues[postalCodeIndex] = currentPostalCode;
          newURL.pathname = pathNameValues.join('/');
          url = newURL.href;
        }
      }
    }
    return url;
  }

  /*
  * Updates the URL function according to the filters
  * @param {Object} List of parameters.
  * @param {String} String Current url value.
  * returns The new URL with the Function url updated.
  */
  function updateUriSegment(data, currentUrl) {
    const url = currentUrl || window.location.href.trim('/').replace(/\/$/, '');
    const parametersForUri = uriSegmentConfig.getUriSegmentHierarchy(data.parameters);
    const indexOfHomeStatus = parametersForUri.indexOf('HomeStatus');

    if (indexOfHomeStatus >= 0) {
      // Hack, HomeStatus is a special parameter as its value is composed by multiple combined URLs.
      // HomeStatus can Model and Floorplans at the same time, so we need to translated this.
      // The translation is to grab the latest value, as it will be the first one selected by the user.
      const statusArray = homeStatusList.getHomeStatus(data.parameters.HomeStatus);
      const firstValue = statusArray.pop() || '';
      parametersForUri[indexOfHomeStatus] = firstValue;
    }

    const temporalUriSegmentParameters = parametersForUri.at(-1);

    if (!parametersForUri.includes(uriSegmentParameter)) {
      const newUrlFunction = getUriSegmentRecursive(uriSegments, parametersForUri, data.parameters);
      const hasChanged = searchTypeHelper.updateUrlFunction(newUrlFunction);

      if (hasChanged && temporalUriSegmentParameters !== 'SrpType') {
        uriSegmentParameter = temporalUriSegmentParameters;
      } else if (temporalUriSegmentParameters === 'SrpType') {
        uriSegmentParameter = '';
      }
    } else {
      const segments = searchResultFunctions[data.parameters.SrpType + '__' + uriSegmentParameter]
        ? ['SrpType', uriSegmentParameter]
        : parametersForUri.filter((x) => x !== uriSegmentParameter);
      const newUrlFunction = getUriSegmentRecursive(uriSegments, segments, data.parameters);
      searchTypeHelper.updateUrlFunction(newUrlFunction);
    }

    return url;
  }

  function updateUrl(data) {
    const pageNumber = data.parameters.PageNumber;
    const currentPostalCode = paramsObserver.getParameter('PostalCode');
    const currentSrpType = data.parameters.SrpType;
    let url;

    if (data.isMoreResults && pageNumber) {
      url = updatePaginationUrl(pageNumber);
    } else {
      url = updateUriSegment(data, url);
      url = updatePaginationUrl(1);
      url = updateQueryStringUrl(data, url);

      const isCurrentLocation = url.indexOf('currentlocation');
      if (isCurrentLocation > 0) {
        url = getNewLinkWithQueryStrings(url);
      }
    }

    if (searchTypeHelper.isCommunitySearchType(currentSrpType)
      || searchTypeHelper.isHomeSearchType(currentSrpType)) {
      validateRedirectToBeta(url);
    }
    const newSearchFunction = searchResultFunctions[data.parameters.SrpType];

    if (newSearchFunction === searchResultFunctions.BuilderResults &&
                currentPostalCode !== '') {
      url = updatePostalCodeBuilderUrl(url, currentPostalCode, true);
    } else {
      url = updatePostalCodeBuilderUrl(url, currentPostalCode, false);
    }
    // https://builderhomesite.atlassian.net/browse/BDXNHS-4927
    url = updateBrandParameter(newSearchFunction, url, data);

    if (!url || url === window.location.href) {
      return;
    }

    window.history.pushState(data.parameters, '', url);
  }

  paramsObserver.subscribe('countsHandling', updateTemporalFilters);
  paramsObserver.subscribe('parametersDiscarded', clearTemporalFilters);
  paramsObserver.subscribe('resetInProgress', resetFilters);
  paramsObserver.subscribe('resultsReady', updateUrl);
});

